import BordSelectDropdown from '../BordSelectDropdown/BordSelectDropdown';
import NudosRadioButton from '../../NudosRadioButton/NudosRadioButton';
import { selectDropdownVariants } from '../BordSelectDropdown/BordSelectDropdown.type';
import './BordRadioSelectDropdown.scss';

export interface IBordRadioSelectOption {
  id: string;
  label: string;
  value: string;
  description?: string;
}

export interface IBordRadioSelectDropdownProps {
  label: string;
  options: IBordRadioSelectOption[];
  selectedValue: string;
  onValueChange: (value: string) => void;
  componentSize?: 'small' | 'medium' | 'large' | 'fullWidth';
  placeholder?: string;
  disabled?: boolean;
  customClassName?: string;
  variant?: selectDropdownVariants;
  isOptionsContainerFullHeight?: boolean;
  customClassNameOptionsContainer?: string;
  customClassNameSelectOption?: string;
}

/**
 * Componente funcional de React correspondiente al dropdown de selección con radio buttons de Bord.
 *
 * @property {string} label - Requerido, la etiqueta del input de selección.
 * @property {IBordRadioSelectOption[]} options - Requerido, un array de objetos que contiene las opciones para el input de selección.
 * @property {string} selectedValue - Requerido, el valor de la opción actualmente seleccionada.
 * @property {function(string): void} onValueChange - Requerido, una función callback que se ejecuta cuando el valor seleccionado cambia.
 * @property {'small' | 'medium' | 'large' | 'fullWidth'} componentSize - Opcional, el tamaño del componente. Por defecto es 'medium'.
 * @property {string} placeholder - Opcional, el placeholder del input de selección cuando ninguna opción ha sido seleccionada. Por defecto es 'Selecciona una opción'.
 * @property {boolean} disabled - Opcional, un booleano que indica si el input de selección está desactivado. Si se proporciona un valor, el input se comportará como un campo de solo lectura y los estilos se aplicarán en consecuencia. Por defecto es false.
 * @property {string} customClassName - Opcional, una className para el componente que se utilizará para personalizar estilos.
 * @property {boolean} isOptionsContainerFullHeight - Opcional, booleano que indica si el contenedor de opciones del dropdown debe tener altura completa.
 * @property {string} customClassNameOptionsContainer - Opcional, una className para el contenedor de opciones del dropdown para personalizar estilos.
 * @property {string} customClassNameSelectOption - Opcional, una className para la opción del dropdown para personalizar estilos.
 */
const BordRadioSelectDropdown: React.FC<IBordRadioSelectDropdownProps> = ({
  label,
  options,
  selectedValue,
  onValueChange,
  componentSize = 'medium',
  placeholder = 'Selecciona una opción',
  disabled,
  customClassName,
  variant = 'default',
  isOptionsContainerFullHeight,
  customClassNameOptionsContainer = '',
  customClassNameSelectOption = ''
}) => {
  const formOptionsUI = (): JSX.Element[] => {
    return options.map((option: IBordRadioSelectOption) => {
      const isCurrentlySelected = selectedValue === option.value;
      return (
        <div
          key={`radio-option-${option.id}`}
          className={`bordRadioSelectOption ${customClassNameSelectOption}`}
          onClick={() => !disabled && onValueChange(option.value)}
        >
          <NudosRadioButton
            isButtonActive={isCurrentlySelected}
            componentSize="small"
            handleClickButton={() => !disabled && onValueChange(option.value)}
          />
          <div className="textContainer">
            <div className="optionText">{option.label}</div>
            {option.description && <div className="optionDescription">{option.description}</div>}
          </div>
        </div>
      );
    });
  };

  const getCurrentValue = () => {
    const selectedOption = options.find(option => option.value === selectedValue);
    return selectedOption?.label || placeholder;
  };

  return (
    <div className={`bordRadioSelectDropdown ${customClassName || ''}`}>
      <BordSelectDropdown
        label={label}
        componentSize={componentSize}
        selectOptions={formOptionsUI()}
        currentValueOrplaceholder={getCurrentValue()}
        isFilled={!!selectedValue}
        isDeactivated={disabled}
        variant={variant}
        isOptionsContainerFullHeight={isOptionsContainerFullHeight}
        customClassNameOptionsContainer={customClassNameOptionsContainer}
      />
    </div>
  );
};

export default BordRadioSelectDropdown;
